import { useState, useEffect } from 'react';

import { useIsMutating } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsServer } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import FormField from '#globalComponents/Forms/FormField';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import Panel from '#globalComponents/Overlay/Panel';
import { emailAccountsMutationKeys } from '#settingsFeature/models/emailAccounts/keys';

const GMAIL_APP_PASSWORD_HELP_URL =
	'https://support.b2b-hero.com/e-mail-kampagnen--einrichtung/71cKaAGTq9w4HsPrDjdm5e/gmail--google-workspace-smtp-einrichten/71cKaAGTq95UESinxvH7Ey';

const PROVIDER_CONFIGS = {
	custom: {
		name: 'Server',
		smtpHost: '',
		smtpPort: '',
		smtpUseTls: true,
	},
	gmail: {
		name: 'Gmail / Google Workspace',
		smtpHost: 'smtp.gmail.com',
		smtpPort: 465,
		smtpUseTls: true,
	},
	strato: {
		name: 'Strato',
		smtpHost: 'smtp.strato.de',
		smtpPort: 465,
		smtpUseTls: true,
	},
	ionos: {
		name: 'IONOS (1&1)',
		smtpHost: 'smtp.ionos.de',
		smtpPort: 465,
		smtpUseTls: true,
	},
};

function validateSmtpForm(formData, isEditMode) {
	const errors = {};

	if (!formData.smtpHost?.trim()) {
		errors.smtpHost = 'Bitte geben Sie den SMTP-Server ein';
	}
	if (!formData.emailAddress?.trim()) {
		errors.emailAddress = 'Bitte geben Sie eine E-Mail Adresse ein';
	} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
		errors.emailAddress = 'Bitte geben Sie eine gültige E-Mail Adresse ein';
	}

	if (!isEditMode && !formData.smtpPassword?.trim()) {
		errors.smtpPassword = 'Bitte geben Sie ein Passwort ein';
	}

	if (!formData.smtpPort) {
		errors.smtpPort = 'Bitte geben Sie einen Port ein';
	} else if (
		isNaN(formData.smtpPort) ||
		parseInt(formData.smtpPort, 10) < 1 ||
		parseInt(formData.smtpPort, 10) > 65535
	) {
		errors.smtpPort = 'Bitte geben Sie einen gültigen Port ein (z.B. 465 oder 587)';
	}

	return errors;
}

function buildSmtpPayload(formData, isEditMode, accountId) {
	const payload = {
		smtpHost: formData.smtpHost.trim(),
		emailAddress: formData.emailAddress.trim(),
		senderName: formData.senderName?.trim() || null,
		smtpUsername: formData.smtpUsername?.trim() || formData.emailAddress.trim(),
		smtpPort: parseInt(formData.smtpPort, 10),
		smtpUseTls: formData.smtpUseTls,
		provider: 'generic',
	};

	if (!isEditMode) payload.authStrategy = 'password';
	if (formData.smtpPassword?.trim()) payload.smtpPassword = formData.smtpPassword.trim();
	if (isEditMode && accountId) payload.accountId = accountId;

	return payload;
}

const DEFAULT_FORM_DATA = {
	senderName: '',
	smtpHost: '',
	emailAddress: '',
	smtpUsername: '',
	smtpPassword: '',
	smtpUseTls: true,
	smtpPort: '',
	provider: 'generic',
};

const SmtpFormStep = ({ account, onSave, onBack, onClose, submitRef }) => {
	const isSavingCreate = useIsMutating({ mutationKey: emailAccountsMutationKeys.create });
	const isSavingUpdate = useIsMutating({ mutationKey: emailAccountsMutationKeys.update });
	const isSaving = isSavingCreate > 0 || isSavingUpdate > 0;
	const isEditMode = Boolean(account);

	const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (account) {
			setFormData({
				senderName: account.senderName || '',
				smtpHost: account.smtpHost || '',
				emailAddress: account.emailAddress || '',
				smtpUsername: account.smtpUsername || '',
				smtpPassword: '',
				smtpUseTls: account.smtpUseTls !== undefined ? account.smtpUseTls : true,
				smtpPort: account.smtpPort?.toString() || '',
				provider: account.provider || 'generic',
			});
		} else {
			setFormData(DEFAULT_FORM_DATA);
		}
		setErrors({});
	}, [account]);

	const handleFormInput = (e) => {
		const { name, type, value, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: null }));
		}
	};

	const handleProviderChange = (e) => {
		const selectedProvider = e.target.value;
		const config = PROVIDER_CONFIGS[selectedProvider];

		if (config) {
			setFormData((prev) => ({
				...prev,
				provider: selectedProvider,
				smtpHost: config.smtpHost || prev.smtpHost,
				smtpPort: config.smtpPort ? config.smtpPort.toString() : prev.smtpPort,
				smtpUseTls: config.smtpUseTls !== undefined ? config.smtpUseTls : prev.smtpUseTls,
			}));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const newErrors = validateSmtpForm(formData, isEditMode);
		setErrors(newErrors);
		if (Object.keys(newErrors).length > 0) return;

		onSave(buildSmtpPayload(formData, isEditMode, account?.id));
	};

	// The dialog surface itself is the <form>, so the router submits through this handler.
	useEffect(() => {
		submitRef.current = handleSubmit;
	});

	return (
		<>
			<Panel.Header
				icon={BsServer}
				subtitle={
					isEditMode
						? 'Bearbeiten Sie die Details Ihres SMTP-Kontos'
						: 'Geben Sie die SMTP-Server Details für Ihr E-Mail Konto ein'
				}
				onClose={onClose}
			>
				{isEditMode ? 'SMTP-Konto bearbeiten' : 'Eigenen SMTP konfigurieren'}
			</Panel.Header>

			<Panel.Body>
				<div className="space-y-4">
					<FormField
						label="E-Mail-Provider"
						htmlFor="provider"
						hint="Wählen Sie Ihren Provider aus, um die SMTP-Einstellungen automatisch zu befüllen"
					>
						<Select id="provider" name="provider" value={formData.provider} onChange={handleProviderChange}>
							<option value="custom">{PROVIDER_CONFIGS.custom.name}</option>
							<option value="gmail">{PROVIDER_CONFIGS.gmail.name}</option>
							<option value="strato">{PROVIDER_CONFIGS.strato.name}</option>
							<option value="ionos">{PROVIDER_CONFIGS.ionos.name}</option>
						</Select>
					</FormField>

					{formData.provider === 'gmail' && (
						<Alert tone="info" title="Gmail benötigt ein App-Passwort">
							Verwenden Sie nicht Ihr normales Google-Passwort, sondern ein App-Passwort.{' '}
							<a
								href={GMAIL_APP_PASSWORD_HELP_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="font-semibold underline"
							>
								So richten Sie das App-Passwort ein
							</a>
						</Alert>
					)}

					<div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
						<FormField label="SMTP-Server *" htmlFor="smtpHost" error={errors.smtpHost}>
							<TextInput
								id="smtpHost"
								name="smtpHost"
								placeholder="smtp.example.com"
								value={formData.smtpHost}
								onChange={handleFormInput}
								error={Boolean(errors.smtpHost)}
							/>
						</FormField>
						<FormField label="Port *" htmlFor="smtpPort" error={errors.smtpPort} className="md:w-24">
							<TextInput
								type="number"
								id="smtpPort"
								name="smtpPort"
								placeholder="465"
								value={formData.smtpPort}
								onChange={handleFormInput}
								error={Boolean(errors.smtpPort)}
							/>
						</FormField>
					</div>

					<FormField
						label="Absendername"
						htmlFor="senderName"
						hint="Wird dem Empfänger als Absender angezeigt"
					>
						<TextInput
							id="senderName"
							name="senderName"
							placeholder="z.B. Max Mustermann"
							value={formData.senderName}
							onChange={handleFormInput}
						/>
					</FormField>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<FormField label="E-Mail-Adresse *" htmlFor="emailAddress" error={errors.emailAddress}>
							<TextInput
								type="email"
								id="emailAddress"
								name="emailAddress"
								placeholder="info@example.com"
								value={formData.emailAddress}
								onChange={handleFormInput}
								error={Boolean(errors.emailAddress)}
							/>
						</FormField>
						<FormField label="SMTP-Benutzername" htmlFor="smtpUsername">
							<TextInput
								id="smtpUsername"
								name="smtpUsername"
								placeholder="Optional - nutzt E-Mail wenn leer"
								value={formData.smtpUsername}
								onChange={handleFormInput}
							/>
						</FormField>
					</div>

					<FormField
						label={isEditMode ? 'Passwort (leer lassen, um unverändert zu lassen)' : 'Passwort *'}
						htmlFor="smtpPassword"
						error={errors.smtpPassword}
					>
						<TextInput
							type="password"
							id="smtpPassword"
							name="smtpPassword"
							placeholder={isEditMode ? 'Neues Passwort eingeben...' : 'Passwort'}
							value={formData.smtpPassword}
							onChange={handleFormInput}
							error={Boolean(errors.smtpPassword)}
						/>
					</FormField>

					<div>
						<Checkbox
							id="smtpUseTls"
							label="TLS nutzen"
							checked={formData.smtpUseTls}
							onChange={(checked) => setFormData((prev) => ({ ...prev, smtpUseTls: checked }))}
						/>
						<p className="ml-6 mt-1 text-xs text-bgBase-800">
							Empfohlen für sichere Verbindungen (Port 587 oder 465)
						</p>
					</div>
				</div>
			</Panel.Body>

			<Panel.Footer>
				{onBack && (
					<Button variant="outline" size="md" onClick={onBack} disabled={isSaving}>
						Zurück
					</Button>
				)}
				<Button variant="solid" size="md" type="submit" loading={isSaving} className="ml-auto">
					{isEditMode ? 'Änderungen speichern' : 'Konto hinzufügen'}
				</Button>
			</Panel.Footer>
		</>
	);
};

SmtpFormStep.propTypes = {
	account: PropTypes.object, // null for new account, object for editing
	onSave: PropTypes.func.isRequired,
	onBack: PropTypes.func,
	onClose: PropTypes.func,
	submitRef: PropTypes.shape({ current: PropTypes.func }).isRequired,
};

export default SmtpFormStep;
